// hooks/useArrowGrid.js
import React, { useRef, useCallback, useEffect, useState } from 'react';
import { normaliseKey } from '../utils/normalizeKeys';

/**
 * Arrow-key grid navigation with optional throttling.
 *
 * @param {Object}   opts
 * @param {Function} opts.setIsOpen
 * @param {boolean}  opts.isOpen
 * @param {number}   opts.rowStart
 * @param {number}   opts.rows
 * @param {Function} opts.colsInRow
 * @param {Function} [opts.onExitUp]
 * @param {Function} [opts.onExitDown]
 * @param {Function} [opts.onActivity]
 * @param {boolean}  [opts.enabled=true]
 * @param {Function} [opts.onExitRight]
 * @param {Function} [opts.onExitLeft]
 * @param {Function} [opts.onExitBack]
 * @param {Function} [opts.isBackKey]
 * @param {boolean}  [opts.resetColOnRowChange=false]
 * @param {boolean}  [opts.isMainPanel=false]
 * @param {number}   [opts.repeatDelay=300]  // NEW ─ min ms between moves
 */
export default function useArrowGrid({
  setIsOpen,
  isOpen,
  rowStart = 0,
  rows,
  colsInRow,
  onExitUp,
  onExitDown,
  onActivity,
  enabled = true,
  onExitRight,
  onExitLeft,
  onExitBack,
  isBackKey,
  resetColOnRowChange = false,
  isMainPanel = false,
  repeatDelay = 0,            // ← NEW PARAM
}) {
  const first   = rowStart;
  const last    = rowStart + rows - 1;
  const [cursor, setCursor] = useState({
    row: first,
    col: isMainPanel ? -1 : 1,
  });

  /* ───────────── throttle bookkeeping */
  const lastMoveRef = useRef(0);

  /* ───────────── core move handler  */
  const move = useCallback(
    (key) => {
      if (!enabled) return;

      const now = Date.now();
      if (now - lastMoveRef.current < repeatDelay) return;  // THROTTLE
      lastMoveRef.current = now;

      onActivity?.();
      let { row, col } = cursor;

      if (!isOpen) setIsOpen(true);

      if (onExitBack && isBackKey && isBackKey(key)) {
        onExitBack();
        return;
      }

      switch (key) {
        case 'ArrowLeft':
          if (col === 0) {
            onExitLeft?.();
            return;
          }
          col = Math.max(0, col - 1);
          break;

        case 'ArrowRight':
          if (col === colsInRow(row) - 1) {
            onExitRight?.();
            return;
          }
          col = Math.min(colsInRow(row) - 1, col + 1);
          break;

        case 'ArrowDown': {
          if (row === last) return;
          const nextRow = row + 1;
          const nextCol = resetColOnRowChange
            ? 0
            : Math.min(col, colsInRow(nextRow) - 1);
          setCursor({ row: nextRow, col: nextCol });
          return;
        }

        case 'ArrowUp': {
          if (row === first) return;
          const nextRow = row - 1;
          const nextCol = resetColOnRowChange
            ? 0
            : Math.min(col, colsInRow(nextRow) - 1);
          setCursor({ row: nextRow, col: nextCol });
          return;
        }

        case 'Backspace':
        case 'Escape':
        case 'SoftLeft':
          onExitBack?.();
          return;

        default:
          return;
      }

      setCursor({ row, col });
    },
    [
      cursor,
      colsInRow,
      first,
      last,
      onExitUp,
      onExitDown,
      onActivity,
      onExitBack,
      isBackKey,
      enabled,
      isOpen,
      setIsOpen,
      repeatDelay,
    ]
  );

  /* ───────────── key listeners */
  useEffect(() => {
    const listener = (e) => {
      if (e.key === 'Tab') return;
      const key = normaliseKey(e.key || e.detail?.action || e.detail?.key);
      move(key);
      e.stopPropagation();
    };

    document.addEventListener('keydown', listener, true);
    document.addEventListener('tvremote', listener, true);

    return () => {
      document.removeEventListener('keydown', listener, true);
      document.removeEventListener('tvremote', listener, true);
    };
  }, [move]);

  return { cursor, setCursor, handleKey: move };
}
